import React, { useCallback, useState } from 'react';
import css from './ShopSettingsForm.module.css';
import { Button, CustomModal, FieldCurrencyInput, FieldTextInput } from '../../../components';
import {
  composeValidators,
  getPriceValidators,
  maxLength,
  required,
} from '../../../util/validators';
import appSettings from '../../../config/settings';
import { isArrayLength } from '../../../util/genericHelpers';
import { showErrorToast, showSuccessToast } from '../../../util/toast';
import { useFormState } from 'react-final-form';
import { queryOwnListings, updateListing } from '../ShopSettingsPage.duck';
import { useSelector } from 'react-redux';
import { currentUserSelector } from '../../../ducks/user.duck';
const TITLE_MAX_LENGTH = 60;
const RESULT_PAGE_SIZE = 30;

const EditListingModal = ({
  isOpen,
  onClose,
  dispatch,
  intl,
  config,
  selectedItems,
  form,
  isShopifyAccountPage,
}) => {
  const currentUser = useSelector(currentUserSelector);
  const currentUserId = currentUser?.id?.uuid;
  const { formatMessage } = intl;
  const [inProgress, setInProgress] = useState(false);
  const selectedIds = Array.from(selectedItems);
  const { invalid, values } = useFormState();
  const submitDisabled = invalid || inProgress;
  const marketplaceCurrency = config.currency;
  const listingMinimumPriceSubUnits = config.listingMinimumPriceSubUnits;
  const titleRequiredMessage = formatMessage({
    id: 'EditListingDetailsForm.titleRequired',
  });
  const maxLengthMessage = formatMessage(
    { id: 'EditListingDetailsForm.maxLength' },
    {
      maxLength: TITLE_MAX_LENGTH,
    }
  );
  const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);
  const priceValidators = getPriceValidators(
    listingMinimumPriceSubUnits,
    marketplaceCurrency,
    intl
  );
  // Async handler for publishing drafts
  const updateListings = useCallback(async () => {
    if (isArrayLength(selectedIds)) {
      setInProgress(true);
      try {
        const { title, description, price } = values || {};
        await Promise.all(
          selectedIds.map(id =>
            dispatch(
              updateListing({
                listingId: id,
                config,
                title,
                description,
                price,
                isShopifyAccountPage,
                currentUserId,
              })
            )
          )
        );
        dispatch(
          queryOwnListings({
            config,
            page: 1,
            perPage: RESULT_PAGE_SIZE,
            fetchAll: true,
          })
        );
      } catch (error) {
        showErrorToast('Failed to update listing.');
      } finally {
        setInProgress(false);
        onClose();
        form.batch(() => {
          form.change('title', null);
          form.change('description', null);
          form.change('price', null);
        });
      }
    }
  }, [selectedIds, dispatch, config]);
  return (
    <CustomModal
      id="EditListingModal"
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={formatMessage({ id: `EditListingModal.title` })}
    >
      <div className={css.editListingContainer}>
        <FieldTextInput
          id={`title`}
          name="title"
          className={css.title}
          type="text"
          label={formatMessage({
            id: isShopifyAccountPage
              ? 'EditListingDetailsForm.shopifyTitle'
              : 'EditListingDetailsForm.title',
          })}
          placeholder={formatMessage({
            id: isShopifyAccountPage
              ? 'EditListingDetailsForm.shopifyTitlePlaceholder'
              : 'EditListingDetailsForm.titlePlaceholder',
          })}
          maxLength={TITLE_MAX_LENGTH}
          validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
        />

        <FieldTextInput
          id={`description`}
          name="description"
          className={css.description}
          type="textarea"
          label={formatMessage({ id: 'EditListingDetailsForm.description' })}
          placeholder={formatMessage({
            id: 'EditListingDetailsForm.descriptionPlaceholder',
          })}
          validate={required(
            formatMessage({
              id: 'EditListingDetailsForm.descriptionRequired',
            })
          )}
        />
        <FieldCurrencyInput
          id={`price`}
          name="price"
          className={css.input}
          label={formatMessage(
            { id: 'EditListingPricingForm.pricePerProduct' },
            { unitType: 'item' }
          )}
          placeholder={formatMessage({
            id: 'EditListingPricingForm.priceInputPlaceholder',
          })}
          currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
          validate={priceValidators}
        />
        <Button
          className={css.submitButton}
          type="button"
          inProgress={inProgress}
          disabled={submitDisabled}
          onClick={updateListings}
        >
          {formatMessage({ id: 'EditListingDetailsForm.submit' })}
        </Button>
      </div>
    </CustomModal>
  );
};

export default EditListingModal;
